import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Slider,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as ExecuteIcon,
  FileCopy as CloneIcon,
  Save as SaveIcon,
  Visibility as PreviewIcon,
  DragIndicator as DragIcon,
  FilterList as FilterIcon,
  Assessment as ChartIcon,
  TableChart as TableIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'; // Commented out due to React 19 compatibility

interface ReportField {
  id: string;
  label: string;
  dataType: 'string' | 'number' | 'date' | 'boolean' | 'currency' | 'percentage' | 'duration';
  dataSource: {
    collection: string;
    field: string;
    aggregation: 'none' | 'sum' | 'count' | 'average' | 'min' | 'max' | 'distinct';
  };
  formatting: {
    decimalPlaces: number;
    currencySymbol: string;
    dateFormat: string;
    prefix?: string;
    suffix?: string;
  };
  groupBy: boolean;
  sortOrder: number;
  visible: boolean;
  width: number;
}

interface ReportFilter {
  id: string;
  field: string;
  operator: string;
  value: any;
  dataType: string;
  userSelectable: boolean;
  required: boolean;
}

interface CustomReport {
  reportId?: string;
  name: string;
  description: string;
  category: string;
  fields: ReportField[];
  filters: ReportFilter[];
  grouping: {
    enabled: boolean;
    fields: string[];
    showSubtotals: boolean;
    showGrandTotal: boolean;
  };
  sorting: {
    field: string;
    direction: 'asc' | 'desc';
    priority: number;
  }[];
  outputFormat: 'table' | 'chart' | 'both';
  chart: {
    enabled: boolean;
    type: string;
    xAxis?: string;
    yAxis?: string;
    title?: string;
  };
  styling: {
    headerBackgroundColor: string;
    headerTextColor: string;
    alternateRowColor: string;
    borderStyle: string;
    fontSize: number;
  };
}

interface ReportBuilderProps {
  open: boolean;
  onClose: () => void;
  report?: CustomReport;
  onSave: (report: CustomReport) => void;
}

const ReportBuilder: React.FC<ReportBuilderProps> = ({ open, onClose, report, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [reportData, setReportData] = useState<CustomReport>({
    name: '',
    description: '',
    category: 'custom',
    fields: [],
    filters: [],
    grouping: {
      enabled: false,
      fields: [],
      showSubtotals: true,
      showGrandTotal: true
    },
    sorting: [],
    outputFormat: 'table',
    chart: {
      enabled: false,
      type: 'bar'
    },
    styling: {
      headerBackgroundColor: '#1976d2',
      headerTextColor: '#ffffff',
      alternateRowColor: '#f5f5f5',
      borderStyle: 'light',
      fontSize: 12
    }
  });
  const [availableFields, setAvailableFields] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Field dialog state
  const [fieldDialog, setFieldDialog] = useState({
    open: false,
    field: null as ReportField | null,
    isEdit: false
  });

  // Filter dialog state
  const [filterDialog, setFilterDialog] = useState({
    open: false,
    filter: null as ReportFilter | null,
    isEdit: false
  });

  useEffect(() => {
    if (open) {
      if (report) {
        setReportData(report);
      } else {
        // Reset to default
        setReportData({
          name: '',
          description: '',
          category: 'custom',
          fields: [],
          filters: [],
          grouping: {
            enabled: false,
            fields: [],
            showSubtotals: true,
            showGrandTotal: true
          },
          sorting: [],
          outputFormat: 'table',
          chart: {
            enabled: false,
            type: 'bar'
          },
          styling: {
            headerBackgroundColor: '#1976d2',
            headerTextColor: '#ffffff',
            alternateRowColor: '#f5f5f5',
            borderStyle: 'light',
            fontSize: 12
          }
        });
      }
      loadAvailableFields();
    }
  }, [open, report]);

  const loadAvailableFields = async () => {
    try {
      const response = await fetch('/api/report-builder/meta/data-sources', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setAvailableFields(data);
    } catch (error) {
      console.error('Failed to load available fields:', error);
    }
  };

  const handleSave = async () => {
    const errors = validateReport();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      onSave(reportData);
      onClose();
    } catch (error) {
      console.error('Failed to save report:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateReport = (): string[] => {
    const errors: string[] = [];
    
    if (!reportData.name.trim()) {
      errors.push('Report name is required');
    }
    
    if (reportData.fields.length === 0) {
      errors.push('At least one field is required');
    }
    
    if (reportData.outputFormat === 'chart' || reportData.outputFormat === 'both') {
      if (!reportData.chart.enabled) {
        errors.push('Chart output selected but chart is not enabled');
      }
      if (reportData.chart.enabled && !reportData.chart.xAxis) {
        errors.push('Chart X-axis field is required');
      }
    }
    
    return errors;
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/report-builder/${reportData.reportId}/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          filters: {},
          format: 'json'
        })
      });
      const data = await response.json();
      setPreviewData(data);
    } catch (error) {
      console.error('Failed to preview report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop temporarily disabled due to React 19 compatibility
  // const handleFieldDragEnd = (result: any) => {
  //   if (!result.destination) return;
  //   const items = Array.from(reportData.fields);
  //   const [reorderedItem] = items.splice(result.source.index, 1);
  //   items.splice(result.destination.index, 0, reorderedItem);
  //   setReportData({
  //     ...reportData,
  //     fields: items.map((item, index) => ({
  //       ...item,
  //       sortOrder: index
  //     }))
  //   });
  // };

  const addField = () => {
    setFieldDialog({
      open: true,
      field: {
        id: `field_${Date.now()}`,
        label: '',
        dataType: 'string',
        dataSource: {
          collection: 'reservations',
          field: '',
          aggregation: 'none'
        },
        formatting: {
          decimalPlaces: 2,
          currencySymbol: '$',
          dateFormat: 'MM/DD/YYYY'
        },
        groupBy: false,
        sortOrder: reportData.fields.length,
        visible: true,
        width: 100
      },
      isEdit: false
    });
  };

  const editField = (index: number) => {
    setFieldDialog({
      open: true,
      field: { ...reportData.fields[index] },
      isEdit: true
    });
  };

  const saveField = (field: ReportField) => {
    if (fieldDialog.isEdit) {
      const updatedFields = reportData.fields.map(f => 
        f.id === field.id ? field : f
      );
      setReportData({ ...reportData, fields: updatedFields });
    } else {
      setReportData({
        ...reportData,
        fields: [...reportData.fields, field]
      });
    }
    setFieldDialog({ open: false, field: null, isEdit: false });
  };

  const deleteField = (index: number) => {
    const updatedFields = reportData.fields.filter((_, i) => i !== index);
    setReportData({ ...reportData, fields: updatedFields });
  };

  const addFilter = () => {
    setFilterDialog({
      open: true,
      filter: {
        id: `filter_${Date.now()}`,
        field: '',
        operator: 'equals',
        value: '',
        dataType: 'string',
        userSelectable: false,
        required: false
      },
      isEdit: false
    });
  };

  const saveFilter = (filter: ReportFilter) => {
    if (filterDialog.isEdit) {
      const updatedFilters = reportData.filters.map(f => 
        f.id === filter.id ? filter : f
      );
      setReportData({ ...reportData, filters: updatedFilters });
    } else {
      setReportData({
        ...reportData,
        filters: [...reportData.filters, filter]
      });
    }
    setFilterDialog({ open: false, filter: null, isEdit: false });
  };

  const deleteFilter = (index: number) => {
    const updatedFilters = reportData.filters.filter((_, i) => i !== index);
    setReportData({ ...reportData, filters: updatedFilters });
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Typography variant="h6">
          {report ? 'Edit Report' : 'Create Custom Report'}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationErrors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Basic Info" />
          <Tab label="Fields" />
          <Tab label="Filters" />
          <Tab label="Grouping & Sorting" />
          <Tab label="Output & Styling" />
          <Tab label="Preview" />
        </Tabs>

        {/* Basic Info Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Report Name"
                value={reportData.name}
                onChange={(e) => setReportData({ ...reportData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={reportData.category}
                  onChange={(e) => setReportData({ ...reportData, category: e.target.value })}
                >
                  <MenuItem value="financial">Financial</MenuItem>
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="guest">Guest</MenuItem>
                  <MenuItem value="marketing">Marketing</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={reportData.description}
                onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Fields Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Report Fields</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addField}
            >
              Add Field
            </Button>
          </Box>

          <List>
            {reportData.fields.map((field, index) => (
              <ListItem
                key={field.id}
                sx={{ border: 1, borderColor: 'divider', mb: 1 }}
              >
                <ListItemText
                  primary={field.label}
                  secondary={`${field.dataSource.collection}.${field.dataSource.field} (${field.dataType})`}
                />
                <Chip
                  label={field.dataSource.aggregation}
                  size="small"
                  color={field.dataSource.aggregation !== 'none' ? 'primary' : 'default'}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={() => editField(index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => deleteField(index)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Filters Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Report Filters</Typography>
            <Button
              variant="contained"
              startIcon={<FilterIcon />}
              onClick={addFilter}
            >
              Add Filter
            </Button>
          </Box>

          <List>
            {reportData.filters.map((filter, index) => (
              <ListItem key={filter.id} sx={{ border: 1, borderColor: 'divider', mb: 1 }}>
                <ListItemText
                  primary={`${filter.field} ${filter.operator} ${filter.value}`}
                  secondary={`Type: ${filter.dataType} | User Selectable: ${filter.userSelectable ? 'Yes' : 'No'}`}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={() => setFilterDialog({ open: true, filter, isEdit: true })}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => deleteFilter(index)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Grouping & Sorting Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reportData.grouping.enabled}
                    onChange={(e) => setReportData({
                      ...reportData,
                      grouping: { ...reportData.grouping, enabled: e.target.checked }
                    })}
                  />
                }
                label="Enable Grouping"
              />
            </Grid>
            
            {reportData.grouping.enabled && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Group By Fields</InputLabel>
                    <Select
                      multiple
                      value={reportData.grouping.fields}
                      onChange={(e) => setReportData({
                        ...reportData,
                        grouping: { ...reportData.grouping, fields: e.target.value as string[] }
                      })}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {reportData.fields.map((field) => (
                        <MenuItem key={field.id} value={field.id}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reportData.grouping.showSubtotals}
                        onChange={(e) => setReportData({
                          ...reportData,
                          grouping: { ...reportData.grouping, showSubtotals: e.target.checked }
                        })}
                      />
                    }
                    label="Show Subtotals"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reportData.grouping.showGrandTotal}
                        onChange={(e) => setReportData({
                          ...reportData,
                          grouping: { ...reportData.grouping, showGrandTotal: e.target.checked }
                        })}
                      />
                    }
                    label="Show Grand Total"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Output & Styling Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Output Format</InputLabel>
                <Select
                  value={reportData.outputFormat}
                  onChange={(e) => setReportData({ ...reportData, outputFormat: e.target.value as any })}
                >
                  <MenuItem value="table">Table Only</MenuItem>
                  <MenuItem value="chart">Chart Only</MenuItem>
                  <MenuItem value="both">Table and Chart</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reportData.chart.enabled}
                    onChange={(e) => setReportData({
                      ...reportData,
                      chart: { ...reportData.chart, enabled: e.target.checked }
                    })}
                  />
                }
                label="Enable Chart"
              />
            </Grid>

            {reportData.chart.enabled && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      value={reportData.chart.type}
                      onChange={(e) => setReportData({
                        ...reportData,
                        chart: { ...reportData.chart, type: e.target.value }
                      })}
                    >
                      <MenuItem value="bar">Bar Chart</MenuItem>
                      <MenuItem value="line">Line Chart</MenuItem>
                      <MenuItem value="pie">Pie Chart</MenuItem>
                      <MenuItem value="doughnut">Doughnut Chart</MenuItem>
                      <MenuItem value="area">Area Chart</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>X-Axis Field</InputLabel>
                    <Select
                      value={reportData.chart.xAxis || ''}
                      onChange={(e) => setReportData({
                        ...reportData,
                        chart: { ...reportData.chart, xAxis: e.target.value }
                      })}
                    >
                      {reportData.fields.map((field) => (
                        <MenuItem key={field.id} value={field.id}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Y-Axis Field</InputLabel>
                    <Select
                      value={reportData.chart.yAxis || ''}
                      onChange={(e) => setReportData({
                        ...reportData,
                        chart: { ...reportData.chart, yAxis: e.target.value }
                      })}
                    >
                      {reportData.fields.filter(f => f.dataType === 'number' || f.dataType === 'currency').map((field) => (
                        <MenuItem key={field.id} value={field.id}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Divider sx={{ width: '100%', my: 2 }} />
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Styling Options</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Header Background Color"
                type="color"
                value={reportData.styling.headerBackgroundColor}
                onChange={(e) => setReportData({
                  ...reportData,
                  styling: { ...reportData.styling, headerBackgroundColor: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Header Text Color"
                type="color"
                value={reportData.styling.headerTextColor}
                onChange={(e) => setReportData({
                  ...reportData,
                  styling: { ...reportData.styling, headerTextColor: e.target.value }
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography gutterBottom>Font Size: {reportData.styling.fontSize}px</Typography>
              <Slider
                value={reportData.styling.fontSize}
                onChange={(_, value) => setReportData({
                  ...reportData,
                  styling: { ...reportData.styling, fontSize: value as number }
                })}
                min={8}
                max={20}
                marks
                valueLabelDisplay="auto"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Preview Tab */}
        <TabPanel value={activeTab} index={5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Report Preview</Typography>
            <Button
              variant="contained"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={!reportData.reportId || loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Generate Preview'}
            </Button>
          </Box>
          
          {previewData && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {reportData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Generated: {new Date(previewData.generatedAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Total Rows: {previewData.totalRows}
                </Typography>
                
                {previewData.data.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: reportData.styling.headerBackgroundColor }}>
                          {previewData.metadata.fields.map((field: any) => (
                            <th
                              key={field.id}
                              style={{
                                color: reportData.styling.headerTextColor,
                                padding: '8px',
                                textAlign: 'left',
                                border: '1px solid #ccc'
                              }}
                            >
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.data.slice(0, 10).map((row: any, index: number) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor: index % 2 === 1 ? reportData.styling.alternateRowColor : 'transparent'
                            }}
                          >
                            {previewData.metadata.fields.map((field: any) => (
                              <td
                                key={field.id}
                                style={{
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  fontSize: `${reportData.styling.fontSize}px`
                                }}
                              >
                                {row[field.id] || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewData.data.length > 10 && (
                      <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                        ... and {previewData.data.length - 10} more rows
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          Save Report
        </Button>
      </DialogActions>

      {/* Field Configuration Dialog */}
      <FieldDialog
        open={fieldDialog.open}
        field={fieldDialog.field}
        availableFields={availableFields}
        onClose={() => setFieldDialog({ open: false, field: null, isEdit: false })}
        onSave={saveField}
      />

      {/* Filter Configuration Dialog */}
      <FilterDialog
        open={filterDialog.open}
        filter={filterDialog.filter}
        availableFields={availableFields}
        onClose={() => setFilterDialog({ open: false, filter: null, isEdit: false })}
        onSave={saveFilter}
      />
    </Dialog>
  );
};

// Field Configuration Dialog Component
const FieldDialog: React.FC<{
  open: boolean;
  field: ReportField | null;
  availableFields: any;
  onClose: () => void;
  onSave: (field: ReportField) => void;
}> = ({ open, field, availableFields, onClose, onSave }) => {
  const [fieldData, setFieldData] = useState<ReportField | null>(null);

  useEffect(() => {
    if (field) {
      setFieldData({ ...field });
    }
  }, [field]);

  const handleSave = () => {
    if (fieldData) {
      onSave(fieldData);
    }
  };

  if (!fieldData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Field</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Field Label"
              value={fieldData.label}
              onChange={(e) => setFieldData({ ...fieldData, label: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={fieldData.dataType}
                onChange={(e) => setFieldData({ ...fieldData, dataType: e.target.value as any })}
              >
                <MenuItem value="string">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="currency">Currency</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="boolean">Yes/No</MenuItem>
                <MenuItem value="percentage">Percentage</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Collection</InputLabel>
              <Select
                value={fieldData.dataSource.collection}
                onChange={(e) => setFieldData({
                  ...fieldData,
                  dataSource: { ...fieldData.dataSource, collection: e.target.value }
                })}
              >
                {Object.keys(availableFields).map((collection) => (
                  <MenuItem key={collection} value={collection}>
                    {collection.charAt(0).toUpperCase() + collection.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Field</InputLabel>
              <Select
                value={fieldData.dataSource.field}
                onChange={(e) => setFieldData({
                  ...fieldData,
                  dataSource: { ...fieldData.dataSource, field: e.target.value }
                })}
              >
                {availableFields[fieldData.dataSource.collection]?.map((field: any) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Aggregation</InputLabel>
              <Select
                value={fieldData.dataSource.aggregation}
                onChange={(e) => setFieldData({
                  ...fieldData,
                  dataSource: { ...fieldData.dataSource, aggregation: e.target.value as any }
                })}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="sum">Sum</MenuItem>
                <MenuItem value="count">Count</MenuItem>
                <MenuItem value="average">Average</MenuItem>
                <MenuItem value="min">Minimum</MenuItem>
                <MenuItem value="max">Maximum</MenuItem>
                <MenuItem value="distinct">Distinct Count</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Column Width"
              type="number"
              value={fieldData.width}
              onChange={(e) => setFieldData({ ...fieldData, width: parseInt(e.target.value) || 100 })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={fieldData.visible}
                  onChange={(e) => setFieldData({ ...fieldData, visible: e.target.checked })}
                />
              }
              label="Visible in Report"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Field</Button>
      </DialogActions>
    </Dialog>
  );
};

// Filter Configuration Dialog Component
const FilterDialog: React.FC<{
  open: boolean;
  filter: ReportFilter | null;
  availableFields: any;
  onClose: () => void;
  onSave: (filter: ReportFilter) => void;
}> = ({ open, filter, availableFields, onClose, onSave }) => {
  const [filterData, setFilterData] = useState<ReportFilter | null>(null);

  useEffect(() => {
    if (filter) {
      setFilterData({ ...filter });
    }
  }, [filter]);

  const handleSave = () => {
    if (filterData) {
      onSave(filterData);
    }
  };

  if (!filterData) return null;

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'between', label: 'Between' },
    { value: 'is_null', label: 'Is Empty' },
    { value: 'is_not_null', label: 'Is Not Empty' }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Filter</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Field Name"
              value={filterData.field}
              onChange={(e) => setFilterData({ ...filterData, field: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                value={filterData.operator}
                onChange={(e) => setFilterData({ ...filterData, operator: e.target.value })}
              >
                {operators.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Filter Value"
              value={filterData.value}
              onChange={(e) => setFilterData({ ...filterData, value: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={filterData.dataType}
                onChange={(e) => setFilterData({ ...filterData, dataType: e.target.value })}
              >
                <MenuItem value="string">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="boolean">Yes/No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterData.userSelectable}
                  onChange={(e) => setFilterData({ ...filterData, userSelectable: e.target.checked })}
                />
              }
              label="Allow users to modify this filter"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterData.required}
                  onChange={(e) => setFilterData({ ...filterData, required: e.target.checked })}
                />
              }
              label="Required filter"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Filter</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportBuilder;